import { useCallback, useMemo, type FC } from "react"
import type { DateTime } from "luxon"

import type { DateRangeDateTime, IDateRange } from "types/date.types"
import type { EntityType } from "types/common.types"

import useSelectOptions from "helpers/hooks/useSelectOptions"
import DateRangePopover from "./DateRangePopover"

interface IDatesPopover {
  updateDateRange: (
    fields: { [key: string]: DateTime | null },
    dateRange: DateRangeDateTime,
  ) => void
  dateRangeDateTime: DateRangeDateTime
  anchorEl: HTMLElement | null
  handleClose: () => void
  open: boolean
  setSelectedDateReference: (dateReferenceName: string) => void
  entity: EntityType
}

const DatesPopover: FC<IDatesPopover> = ({
  updateDateRange,
  dateRangeDateTime,
  anchorEl,
  handleClose,
  open,
  setSelectedDateReference,
  entity,
}) => {
  const { getSelectOptionsByEntity } = useSelectOptions()
  const selectOptions = useMemo(
    () => getSelectOptionsByEntity(entity),
    [entity, getSelectOptionsByEntity],
  )

  const handleApply = useCallback(
    ({ fromDate, toDate }: IDateRange) =>
      updateDateRange({ fromDate, toDate }, dateRangeDateTime),
    [dateRangeDateTime, updateDateRange],
  )

  return (
    <DateRangePopover
      aria-label="dates popover"
      id="dates-popover"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      options={selectOptions}
      range={dateRangeDateTime.range}
      value={dateRangeDateTime}
      entity={entity}
      onSelectReference={setSelectedDateReference}
      onApply={handleApply}
    />
  )
}

export default DatesPopover
